import { Component, ErrorInfo, ReactNode } from 'react';
import { reportClientError } from '../api/clientErrors';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
  reportStatus: 'idle' | 'sending' | 'sent' | 'failed';
}

function isDarkMode(): boolean {
  try {
    const stored = JSON.parse(localStorage.getItem('settings') || '{}');
    if (stored.themeMode === 'light') return false;
    if (stored.themeMode === 'dark') return true;
  } catch { /* ignore */ }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

function getStyles(dark: boolean) {
  const wrap: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: dark ? '#161C24' : '#fff',
    fontFamily:
      "'Public Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: dark ? '#fff' : '#212B36',
  };

  const card: React.CSSProperties = {
    maxWidth: 480,
    width: '100%',
    textAlign: 'center',
  };

  const button: React.CSSProperties = {
    display: 'inline-block',
    padding: '10px 22px',
    margin: '8px',
    border: 0,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  };

  const primary: React.CSSProperties = {
    ...button,
    background: '#0e6dfd',
    color: '#fff',
  };

  const secondary: React.CSSProperties = {
    ...button,
    background: dark ? '#212B36' : '#f4f6f8',
    color: dark ? '#fff' : '#212B36',
  };

  const subtitle: React.CSSProperties = {
    fontSize: 15,
    color: dark ? '#919EAB' : '#637381',
    margin: '0 0 24px',
  };

  return { wrap, card, button, primary, secondary, subtitle };
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null, reportStatus: 'idle' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught error', error, info);
  }

  handleRetry = (): void => {
    this.setState({ error: null, componentStack: null, reportStatus: 'idle' });
  };

  handleReport = async (): Promise<void> => {
    const { error, componentStack } = this.state;
    if (!error) return;
    this.setState({ reportStatus: 'sending' });
    try {
      await reportClientError({
        message: error.message,
        stack: error.stack,
        componentStack: componentStack ?? undefined,
        url: window.location.href,
        userAgent: navigator.userAgent,
        occurredAt: new Date().toISOString(),
      });
      this.setState({ reportStatus: 'sent' });
    } catch {
      this.setState({ reportStatus: 'failed' });
    }
  };

  render(): ReactNode {
    const { error, reportStatus } = this.state;
    if (!error) return this.props.children;

    const s = getStyles(isDarkMode());

    return (
      <div style={s.wrap} role="alert">
        <div style={s.card}>
          <h1 style={{ fontSize: 24, margin: '0 0 12px' }}>Something went wrong</h1>
          <p style={s.subtitle}>
            WeSafe hit an unexpected error. Try again, or send a report so we can fix it.
          </p>
          <button type="button" style={s.primary} onClick={this.handleRetry}>
            Retry
          </button>
          <button
            type="button"
            style={s.secondary}
            onClick={this.handleReport}
            disabled={reportStatus === 'sending' || reportStatus === 'sent'}
          >
            {reportStatus === 'sent'
              ? 'Report sent'
              : reportStatus === 'sending'
                ? 'Sending…'
                : reportStatus === 'failed'
                  ? 'Retry report'
                  : 'Send error report'}
          </button>
        </div>
      </div>
    );
  }
}
